import { Reveal } from "@/components/Reveal";
import { BookButton } from "@/components/Buttons";

const L = (file, name) => ({ file: `/funds/${file}.png`, name });

export const ALL_FUNDS = [
  L("hcf", "HCF"), L("nib-first-choice", "nib First Choice"), L("cbhs", "CBHS Corporate"),
  L("medicare", "Medicare"), L("rt-health", "rt health"), L("latrobe", "Latrobe Health Services"), L("aca-health", "ACA Health Benefits Fund"),
  L("qld-country", "Queensland Country Health Fund"), L("rbhs", "Reserve Bank Health Society"), L("peoplecare", "Peoplecare"),
  L("onemedifund", "onemedifund"), L("gmhba", "GMHBA"), L("frank", "Frank Health Insurance"), L("aia", "AIA Health"),
  L("aami", "AAMI Health"), L("apia", "Apia Health"), L("suncorp", "Suncorp Health"), L("qantas", "Qantas Insurance"),
  L("ing", "ING Health"), L("priceline", "Priceline Health Insurance"), L("iman", "iman Australian Health Plans"),
  L("uhc-global", "United Healthcare Global"), L("real", "Real Insurance"), L("gu-health", "GU Health"),
  L("australian-seniors", "Australian Seniors"), L("dva", "Department of Veterans' Affairs"),
];

const PREFERRED = [L("hcf", "HCF"), L("nib-first-choice", "nib First Choice"), L("cbhs", "CBHS Corporate")];

const PARTNERS = [
  { parent: L("nib-first-choice", "nib First Choice"), members: [L("aami", "AAMI"), L("apia", "Apia"), L("suncorp", "Suncorp"), L("qantas", "Qantas Insurance"), L("ing", "ING"), L("priceline", "Priceline Health Insurance"), L("iman", "iman Australian Health Plans"), L("uhc-global", "United Healthcare Global"), L("real", "Real Insurance"), L("gu-health", "GU Health"), L("australian-seniors", "Australian Seniors")] },
];

const GOVERNMENT = [L("medicare", "Medicare — Child Dental Benefits Schedule"), L("dva", "Australian Government Department of Veterans' Affairs")];

const LogoTile = ({ logo, size = "h-24", testid }) => (
  <div data-testid={testid} title={logo.name} className={`flex ${size} items-center justify-center rounded-lg border border-stone/80 bg-white px-6 py-4`}>
    <img src={logo.file} alt={logo.name} loading="lazy" className="max-h-full max-w-full object-contain" />
  </div>
);

export const HealthFundsMarquee = () => (
  <section id="health-funds" data-testid="health-funds-section" className="border-y border-stone/70 bg-sage py-16 lg:py-20">
    <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-16">
      <Reveal>
        <p className="font-mono text-sm tracking-[0.3em] uppercase text-leafmute">Health funds</p>
        <h2 className="mt-4 font-serif text-4xl tracking-tight text-ink sm:text-5xl">We accept all major health funds.</h2>
        <p className="mt-4 max-w-2xl font-sans text-base leading-relaxed text-fern sm:text-lg">Claim on the spot with HICAPS — bring your card and we'll process the rebate before you leave, so you only pay the gap.</p>
      </Reveal>
    </div>
    <div data-testid="health-funds-marquee" className="mt-10 overflow-hidden" aria-label="Health funds we accept">
      <div className="hidden w-max animate-marquee-slow gap-4 sm:flex">
        {[...ALL_FUNDS, ...ALL_FUNDS].map((f, i) => (
          <div key={`${f.file}-${i}`} className="w-44 shrink-0">
            <LogoTile logo={f} size="h-20" testid={i < ALL_FUNDS.length ? `fund-logo-${f.file.split("/").pop().replace(".png", "")}` : undefined} />
          </div>
        ))}
      </div>
      <div data-testid="health-funds-mobile-list" className="grid grid-cols-2 gap-3 px-5 sm:hidden">
        {ALL_FUNDS.map((f) => (
          <LogoTile key={f.file} logo={f} size="h-16" />
        ))}
      </div>
    </div>
  </section>
);

export const PreferredProvider = () => (
  <section data-testid="preferred-provider-section" className="bg-cream py-16 lg:py-20">
    <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-16">
      <Reveal>
        <p className="font-mono text-sm tracking-[0.3em] uppercase text-leafmute">Preferred provider for</p>
        <h2 className="mt-4 font-serif text-4xl tracking-tight text-ink sm:text-5xl">Bigger rebates, smaller gaps.</h2>
        <p className="mt-4 max-w-2xl font-sans text-base leading-relaxed text-fern sm:text-lg">As a preferred provider, members of these funds typically get higher rebates on check-ups, cleans and other treatment with us.</p>
      </Reveal>
      <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {PREFERRED.map((l, i) => (
          <Reveal key={l.file} delay={0.06 * i}>
            <LogoTile logo={l} size="h-28" testid={`preferred-logo-${l.file.split("/").pop().replace(".png", "")}`} />
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.2}>
        <div data-testid="preferred-rebate-note" className="mt-8 flex flex-col gap-6 rounded-xl border border-stone/80 bg-sage p-7 sm:p-9 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-2xl">
            <p className="font-serif text-2xl text-ink sm:text-3xl">HCF or nib member? You'll pay less here.</p>
            <p className="mt-3 font-sans text-base leading-relaxed text-fern sm:text-lg">As a preferred provider, your check-up and clean is usually fully covered or close to it, and you'll get higher rebates on most other treatment. Bring your card — we claim on the spot, so you only ever pay the gap.</p>
          </div>
          <BookButton testid="preferred-book-button" className="shrink-0">Book with your fund</BookButton>
        </div>
      </Reveal>
    </div>
  </section>
);

export const PartnerCompanies = () => (
  <section data-testid="partner-companies-section" className="bg-sage py-16 lg:py-20">
    <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-16">
      <Reveal>
        <p className="font-mono text-sm tracking-[0.3em] uppercase text-leafmute">Partner companies</p>
        <h2 className="mt-4 font-serif text-4xl tracking-tight text-ink sm:text-5xl">With one of the nib First Choice partners? You're covered here too.</h2>
      </Reveal>
      <div className="mt-10 space-y-12">
        {PARTNERS.map((g) => (
          <div key={g.parent.file} data-testid={`partner-group-${g.parent.file.split("/").pop().replace(".png", "")}`}>
            <div className="flex items-center gap-6">
              <span className="rounded-md border border-stone/80 bg-white px-4 py-2"><img src={g.parent.file} alt={g.parent.name} className="h-8 w-auto object-contain" /></span>
              <span className="h-px flex-1 bg-stone" />
            </div>
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
              {g.members.map((m, i) => (
                <Reveal key={m.file} delay={0.04 * i}>
                  <LogoTile logo={m} size="h-24" />
                </Reveal>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const GovernmentAccepted = () => (
  <section data-testid="government-accepted-section" className="bg-cream py-16 lg:py-20">
    <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-16">
      <div className="grid grid-cols-1 items-center gap-8 rounded-xl border border-stone/80 bg-sage p-8 lg:grid-cols-12 lg:p-12">
        <Reveal className="lg:col-span-5">
          <p className="font-mono text-sm tracking-[0.3em] uppercase text-leafmute">We accept</p>
          <h2 className="mt-4 font-serif text-4xl tracking-tight text-ink sm:text-5xl">Medicare and DVA patients welcome.</h2>
          <p className="mt-4 font-sans text-base leading-relaxed text-fern sm:text-lg">Bulk-billed children's dentistry under the Medicare Child Dental Benefits Schedule for eligible families, and care for Department of Veterans' Affairs card holders.</p>
        </Reveal>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:col-span-7">
          {GOVERNMENT.map((l, i) => (
            <Reveal key={l.file} delay={0.08 * i}>
              <LogoTile logo={l} size="h-32" testid={`government-logo-${l.file.split("/").pop().replace(".png", "")}`} />
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);
